import { FC, memo, useCallback, useEffect, useState } from 'react'

import classnames from 'classnames'
import { LazyLoadImage } from 'react-lazy-load-image-component'

import { ImageAssets } from 'components/Figure/types'
import { useSettings } from 'utils/settings'

import { ImageWrapper } from './styles'

export type ImageProps = {
  assets: ImageAssets
  alt: string
  alignment: 'left' | 'center'
  fitImageToWrapper?: boolean
  crossOrigin?: 'anonymous'
  size?: number
}

export type ImageWithFallbackProps = {
  src: string
  fitImageToWrapper: boolean
  alt: string
  alignment: 'left' | 'center'
  width?: number | 'auto'
  height?: number | 'auto'
  aspectRatio?: string
  srcSet?: string
  sizes?: string
  crossOrigin?: 'anonymous'
}

export const ImageWithFallback = ({
  src,
  srcSet,
  sizes,
  crossOrigin,
  aspectRatio,
  width,
  height,
  fitImageToWrapper,
  alt,
  alignment,
}: ImageWithFallbackProps) => {
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!src) {
      setLoaded(false)
      setError(true)
    }
  }, [src])

  const loadHandler = useCallback(() => {
    setError(false)
    setLoaded(true)
  }, [])

  const errorHandler = useCallback(() => {
    setError(true)
  }, [])
  const cx = classnames({
    'fit-image': fitImageToWrapper,
    loaded,
  })
  let smallerThanWidth = false
  if (width && width < 360 && height && height < 360) {
    smallerThanWidth = true
  }

  return (
    <ImageWrapper
      className="image"
      loaded={loaded}
      error={error}
      style={{ aspectRatio }}
      fitImageToWrapper={fitImageToWrapper}
      alignment={alignment}
    >
      {src && (
        <LazyLoadImage
          src={src}
          alt={alt}
          className={cx}
          crossOrigin={crossOrigin && crossOrigin}
          srcSet={srcSet}
          sizes={sizes}
          onLoad={loadHandler}
          onError={errorHandler}
          width={width}
          height={height}
          style={smallerThanWidth ? { width, height, minHeight: 'auto' } : {}}
        />
      )}
    </ImageWrapper>
  )
}

export const Image: FC<ImageProps> = memo(
  ({
    assets,
    alt,
    crossOrigin,
    size,
    fitImageToWrapper = false,
    alignment = 'left',
  }) => {
    const { settings } = useSettings()

    if (assets && assets.length > 0) {
      if (Array.isArray(assets)) {
        // filter sources by required language.
        // and write out the entry for the srcset.
        // size is handled by the srcset

        let sources = assets
          .flatMap((image) => {
            // Only accept assets in our language, or no language
            if (image.language !== settings.defaultContentLanguage) return []
            // Return the source string for this asset
            if (image.width) {
              return [`${image.url} ${image?.width}w`]
            }
            return [`${image.url}`]
          })
          .join(', ')

        if (sources.length === 0) {
          // Find any language-less assets as a fall-back
          sources += assets
            .flatMap((asset) => {
              if (asset.language !== undefined) return []
              if (asset.width) {
                return [`${asset.url} ${asset?.width}w`]
              }
              return [`${asset.url}`]
            })
            .join(', ')
        }

        const aspectRatio = () => {
          if (assets[0].width !== 'auto' && assets[0].height !== 'auto') {
            return `${assets[0].width / assets[0].height} / 1`
          }
          return undefined
        }

        return (
          <ImageWithFallback
            alignment={alignment}
            fitImageToWrapper={fitImageToWrapper}
            src={assets[0].url}
            width={assets[0].width !== 'auto' ? assets[0].width : undefined}
            alt={alt}
            height={assets[0].height !== 'auto' ? assets[0].height : undefined}
            aspectRatio={aspectRatio()}
            sizes={assets[0].height !== 'auto' && size ? `${size}px` : '100vw'}
            srcSet={sources}
            crossOrigin={crossOrigin && crossOrigin}
          />
        )
      }
    }

    return null
  }
)
